/**
 * Přehledová tabulka pozic (spec 02 FR-1).
 *
 * Řádek = placement. Sloupce nesou n a CI; metriky z tržeb se ve fixture
 * prostředí nezobrazují jako nuly, ale jako prázdné (viz NoRevenueBanner).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useOverview } from '@/api/queries';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import type { OverviewRow } from '@/api/models';
import { FilterBar } from '@/components/FilterBar';
import { HeaderWithTip } from '@/components/InfoTooltip';
import { DataTable } from '@/components/DataTable';
import { useBadgeFilters, type FilterGroup } from '@/components/BadgeFilters';
import { MetricCard, MetricCardGrid } from '@/components/MetricCard';
import { SignificanceBadge } from '@/components/SignificanceBadge';
import { SignalTagList } from '@/components/SignalTag';
import { SmallSampleWarning } from '@/components/SmallSampleWarning';
import { NoRevenueBanner, RevenueUnavailable } from '@/components/NoRevenueData';
import { ErrorState } from '@/components/ErrorState';
import { Sparkline } from '@/charts/Sparkline';
import { toSupplierKey } from '@/charts/palette';
import { hasRevenueData } from '@/lib/revenue';
import {
  formatCi,
  formatCzk,
  formatDeltaPercent,
  formatInt,
  formatNumber,
  formatPercent,
} from '@/lib/formatCz';
import { csvFilename } from '@/lib/csv';

/**
 * Znění tooltipů u sloupců. Formulace odpovídají tomu, jak metriky počítá
 * backend (core/metrics.py, core/stats.py) — CTR interval je Wilson, `ctr_change`
 * je z-test proporcí. Ať UI netvrdí něco jiného než výpočet.
 */
const COLUMN_TIPS = {
  impressions: 'stats_channel.loads — počet zobrazení.',
  ctr:
    'Klik / imprese. Rozsah pod číslem je 95% Wilsonův interval spolehlivosti — čím užší, tím jistější odhad. Klik = klik (metodické rozhodnutí), tj. i mis-kliky do zavíracího rohu.',
  ctr_change:
    'Změna CTR proti srovnávacímu období (předchozí období nebo předchozí týden — volíš u data). Není to prostý rozdíl v %, ale dvouvýběrový z-test proporcí, takže barva a šipka odrážejí i statistickou významnost, ne jen směr.',
  revenue:
    'Odhad jen z vyhraných HB aukcí: součet vítězných CPM / 1000 (CZK). Seznam SSP a přímé kanály v realtime stats cenu nemají, skutečné tržby jsou v AYM. Ve vývojovém/demo prostředí jsou tržby syntetické.',
  ecpm: 'Tržby HB / imprese × 1000 (CZK). Jen HB část, viz Tržby HB.',
  viewability:
    'Podíl impresí, které byly viditelné: view / imprese. view je counter ze stats_channel.',
  fill_rate:
    'Podíl bid requestů, které skončily zobrazenou impresí: imprese / bid_requests. Jen u kanálů s biddingem.',
} as const;

export default function OverviewPage() {
  const { filters } = useUrlFilters();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useOverview(filters);

  // Backend vlajku „měříme tržby" neposílá — odvozuje se z celkových výnosů
  // za období. Viz lib/revenue.ts, proč je nula podezřelá, ne informativní.
  const revenueAvailable = hasRevenueData(data?.totals.revenue);

  // Filtry podle štítků v buňce „Pozice" — stejné štítky, jaké tabulka ukazuje.
  const signalCodes = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of data?.rows ?? []) for (const sg of r.signals ?? []) seen.set(sg.code, sg.label_cs);
    return [...seen];
  }, [data]);
  const filterGroups = useMemo<FilterGroup<OverviewRow>[]>(
    () => [
      {
        key: 'side',
        label: 'Strana',
        options: [
          { value: 'r2b2', label: 'r2b2', test: (r) => !r.group.startsWith('animTest') },
          {
            value: 'px',
            label: 'PX',
            title: 'Konkurence (animTest*)',
            test: (r) => r.group.startsWith('animTest'),
          },
        ],
      },
      {
        key: 'phase',
        label: 'Fáze',
        options: [
          { value: 'first_look', label: 'first-look', test: (r) => r.phase !== 'refresh' },
          { value: 'refresh', label: 'refresh', test: (r) => r.phase === 'refresh' },
        ],
      },
      {
        key: 'debug',
        label: 'Debug logy',
        options: [
          { value: 'yes', label: 'má', test: (r) => r.has_debug_logs },
          { value: 'no', label: 'nemá', test: (r) => !r.has_debug_logs },
        ],
      },
      {
        key: 'registry',
        label: 'Registr',
        options: [
          { value: 'guard', label: 'guard', test: (r) => r.guard },
          { value: 'ab_test', label: 'AB test', test: (r) => r.ab_test },
        ],
      },
      {
        key: 'signal',
        label: 'Signál',
        options: [
          { value: 'any', label: 'jakýkoli', test: (r) => (r.signals ?? []).length > 0 },
          ...signalCodes.map(([code, label]) => ({
            value: code,
            label,
            test: (r: OverviewRow) => (r.signals ?? []).some((sg) => sg.code === code),
          })),
        ],
      },
    ],
    [signalCodes],
  );
  const badges = useBadgeFilters(data?.rows ?? [], filterGroups);

  const columns = useMemo<ColumnDef<OverviewRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Pozice',
        accessorFn: (row) => row.label_cs,
        size: 300,
        cell: ({ row }) => (
          <div className="ov-name">
            <span className="ov-name__main">{row.original.label_cs}</span>
            <div className="ov-name__badges">
              {row.original.supplier === 'B' ? (
                <span className="flag flag--warning" title="Konkurence (animTest*)">
                  PX
                </span>
              ) : null}
              <span className="flag">
                {row.original.phase === 'refresh' ? 'refresh' : 'first-look'}
              </span>
              {row.original.has_debug_logs ? (
                <span className="flag flag--ok" title="Má debug logy → hlubší rozpad v detailu">
                  debug
                </span>
              ) : null}
              {row.original.guard ? (
                <span className="flag" title="Sledováno guardem (registr pozic)">
                  guard
                </span>
              ) : null}
              {row.original.ab_test ? (
                <span className="flag" title="Běží AB test (registr pozic)">
                  AB test
                </span>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'impressions',
        header: () => <HeaderWithTip label="Imprese" tip={COLUMN_TIPS.impressions} />,
        accessorFn: (row) => row.impressions,
        cell: ({ getValue }) => <span className="num">{formatInt(getValue<number>())}</span>,
      },
      {
        // Engaged CTR z přehledové tabulky pryč (zpětná vazba zadavatele) —
        // v detailu pozice zůstává jako dokumentovaná sekundární metrika (02 §5).
        id: 'ctr',
        header: () => <HeaderWithTip label="CTR" tip={COLUMN_TIPS.ctr} />,
        accessorFn: (row) => row.ctr.rate ?? 0,
        cell: ({ row }) => (
          <span className="num ov-ctr">
            {formatPercent(row.original.ctr.rate)}
            <span className="ov-ctr__ci">
              {formatCi(row.original.ctr.ci_low, row.original.ctr.ci_high)}
            </span>
            <SmallSampleWarning
              inline
              successes={row.original.ctr.successes}
              trials={row.original.ctr.trials}
              label={row.original.label_cs}
            />
          </span>
        ),
      },
      {
        id: 'ecpm',
        header: () => <HeaderWithTip label="eCPM HB" tip={COLUMN_TIPS.ecpm} />,
        accessorFn: (row) => row.ecpm ?? -1,
        cell: ({ row }) =>
          !revenueAvailable || row.original.ecpm === null ? (
            <RevenueUnavailable context={revenueAvailable ? 'no_data' : 'fixture'} />
          ) : (
            <span className="num">{formatNumber(row.original.ecpm, 2)}</span>
          ),
      },
      {
        id: 'revenue',
        header: () => <HeaderWithTip label="Tržby HB" tip={COLUMN_TIPS.revenue} />,
        accessorFn: (row) => row.revenue,
        cell: ({ row }) =>
          !revenueAvailable ? (
            <RevenueUnavailable />
          ) : (
            <span className="num">{formatCzk(row.original.revenue)}</span>
          ),
      },
      {
        id: 'viewability',
        header: () => <HeaderWithTip label="Viewability" tip={COLUMN_TIPS.viewability} />,
        accessorFn: (row) => row.viewability ?? -1,
        cell: ({ row }) =>
          row.original.viewability === null ? (
            <span className="num muted">—</span>
          ) : (
            <span className="num">{formatPercent(row.original.viewability, 1)}</span>
          ),
      },
      {
        id: 'fill_rate',
        header: () => <HeaderWithTip label="Fill rate" tip={COLUMN_TIPS.fill_rate} />,
        accessorFn: (row) => row.fill_rate ?? -1,
        cell: ({ row }) =>
          !revenueAvailable || row.original.fill_rate === null ? (
            <RevenueUnavailable context={revenueAvailable ? 'no_data' : 'fixture'} />
          ) : (
            <span className="num">{formatPercent(row.original.fill_rate, 1)}</span>
          ),
      },
      {
        id: 'delta',
        header: () => <HeaderWithTip label="vs předchozí" tip={COLUMN_TIPS.ctr_change} />,
        accessorFn: (row) => row.ctr_change?.diff_relative ?? 0,
        cell: ({ row }) => {
          const change = row.original.ctr_change;
          if (!change) return <span className="num muted">—</span>;
          return (
            <span className="ov-delta">
              <span
                className={`num ${(change.diff_relative ?? 0) > 0 ? 'delta--good' : 'delta--bad'}`}
              >
                {formatDeltaPercent(change.diff_relative)}
              </span>
              <SignificanceBadge comparison={change} compact />
            </span>
          );
        },
      },
      {
        id: 'sparkline',
        header: '14 dní',
        enableSorting: false,
        cell: ({ row }) => (
          <Sparkline
            // Sparkline bere holé hodnoty — z bodů se vytáhne jen CTR.
            values={(row.original.sparkline ?? []).map((point) => point.ctr ?? 0)}
            supplier={toSupplierKey(row.original.supplier)}
            ariaLabel={`Vývoj CTR ${row.original.label_cs} za posledních 14 dní`}
          />
        ),
      },
      {
        id: 'signals',
        header: 'Signály',
        enableSorting: false,
        cell: ({ row }) => <SignalTagList signals={row.original.signals ?? []} />,
      },
    ],
    [revenueAvailable],
  );

  const csvColumns = useMemo(
    () => [
      { key: 'placement_id', header: 'placement_id', value: (r: OverviewRow) => r.placement_id },
      { key: 'domain', header: 'web', value: (r: OverviewRow) => r.domain },
      { key: 'position', header: 'pozice', value: (r: OverviewRow) => r.position },
      { key: 'supplier', header: 'dodavatel', value: (r: OverviewRow) => r.supplier },
      { key: 'phase', header: 'faze', value: (r: OverviewRow) => r.phase },
      { key: 'impressions', header: 'imprese', value: (r: OverviewRow) => r.impressions },
      { key: 'clicks', header: 'kliky', value: (r: OverviewRow) => r.clicks },
      { key: 'ctr', header: 'ctr', value: (r: OverviewRow) => r.ctr.rate },
      { key: 'ctr_ci_low', header: 'ctr_ci_low', value: (r: OverviewRow) => r.ctr.ci_low },
      { key: 'ctr_ci_high', header: 'ctr_ci_high', value: (r: OverviewRow) => r.ctr.ci_high },
      {
        key: 'engaged_ctr',
        header: 'engaged_ctr',
        value: (r: OverviewRow) => (r.engaged_available ? (r.engaged_ctr?.rate ?? null) : null),
      },
      // Tržby jdou do CSV jen když je opravdu měříme — jinak prázdná buňka,
      // ať se nula nedostane do tabulky, kde už kontext chybí.
      {
        key: 'revenue',
        header: 'trzby_hb_odhad_czk',
        value: (r: OverviewRow) => (revenueAvailable ? r.revenue : null),
      },
      {
        key: 'ecpm',
        header: 'ecpm_hb_odhad',
        value: (r: OverviewRow) => (revenueAvailable ? r.ecpm : null),
      },
      { key: 'viewability', header: 'viewability', value: (r: OverviewRow) => r.viewability },
      {
        key: 'fill_rate',
        header: 'fill_rate',
        value: (r: OverviewRow) => (revenueAvailable ? r.fill_rate : null),
      },
      { key: 'p_value', header: 'ctr_zmena_p', value: (r: OverviewRow) => r.ctr_change?.p_value ?? null },
      {
        key: 'signals',
        header: 'signaly',
        value: (r: OverviewRow) => (r.signals ?? []).map((s) => s.code).join('|'),
      },
    ],
    [revenueAvailable],
  );

  if (isError) {
    return (
      <div className="page">
        <h1 className="page__title">Přehled pozic</h1>
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">Přehled pozic</h1>
      </div>

      <FilterBar show={['date', 'placements']} />

      {data && !revenueAvailable ? <NoRevenueBanner /> : null}

      {data ? (
        <MetricCardGrid>
          <MetricCard label="Pozic" value={formatInt(data.totals.placements)} />
          <MetricCard label="Imprese" value={formatInt(data.totals.impressions)} />
          <MetricCard label="Kliky" value={formatInt(data.totals.clicks)} />
          <MetricCard
            label="CTR"
            value={formatPercent(data.totals.ctr.rate)}
            n={data.totals.ctr.trials}
            ciLow={data.totals.ctr.ci_low}
            ciHigh={data.totals.ctr.ci_high}
          />
          <MetricCard
            label="Tržby HB (odhad)"
            value={revenueAvailable ? formatCzk(data.totals.revenue) : null}
            revenueUnavailable={!revenueAvailable}
          />
          <MetricCard
            label="eCPM HB (odhad)"
            value={revenueAvailable ? formatNumber(data.totals.ecpm, 2) : null}
            revenueUnavailable={!revenueAvailable}
          />
        </MetricCardGrid>
      ) : null}

      {data ? badges.bar : null}

      {isLoading ? (
        <div className="skeleton skeleton--table" aria-label="Načítám tabulku" />
      ) : (
        <DataTable
          data={badges.filtered}
          columns={columns}
          csvColumns={csvColumns}
          csvFilename={csvFilename('stickyiq_prehled', filters.date_from, filters.date_to)}
          onRowClick={(row) => navigate(`/position/${encodeURIComponent(row.placement_id)}`)}
          getRowId={(row) => row.placement_id}
          initialSorting={[{ id: 'impressions', desc: true }]}
          emptyTitle="Za zvolené období nejsou žádné pozice"
          emptyDescription={
            <p>
              Zkus širší období nebo zruš část filtrů. Pokud je prázdné i bez filtrů,
              zkontroluj čerstvost ETL v hlavičce — data se možná ještě nenamaterializovala.
            </p>
          }
        />
      )}
    </div>
  );
}
